import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  IconButton,
  Sheet,
  Divider,
} from '@mui/joy';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import {
  Plus as AddIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  Copy as CopyIcon,
  UsersThree as GroupsIcon,
  ChartBar as StatsIcon,
  UserPlus as PersonAddIcon,
  UserMinus as PersonRemoveIcon,
} from '@phosphor-icons/react';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulingApi } from '../../lib/apiScheduling';
import { apiClient } from '../../lib/api';
import type { Team, TeamForm } from '../../types/scheduling';
import type { User } from '../../types';

export const TeamManagementPage: React.FC = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const queryClient = useQueryClient();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [meetingTitle, setMeetingTitle] = useState('Team Meeting');
  const [meetingLocation, setMeetingLocation] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [distributionMode, setDistributionMode] = useState<'equal' | 'maximize'>('equal');
  const [meetingDuration, setMeetingDuration] = useState(30);

  // Fetch teams
  const { data: teams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ['schedulingTeams'],
    queryFn: schedulingApi.getTeams,
  });

  // Fetch organization users for member selection
  const { data: orgUsers = [] } = useQuery({
    queryKey: ['organizationUsers'],
    queryFn: async () => {
      const response = await apiClient.get<{ results: User[] }>('/api/users/');
      return response.data.results || response.data;
    },
  });

  // Fetch team stats
  const { data: teamStats, isLoading: statsLoading } = useQuery({
    queryKey: ['teamStats', selectedTeam?.id],
    queryFn: () => selectedTeam ? schedulingApi.getTeamStats(selectedTeam.id, 30) : null,
    enabled: !!selectedTeam && statsModalOpen,
  });

  // Create team mutation
  const createMutation = useMutation({
    mutationFn: (data: TeamForm) => schedulingApi.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedulingTeams'] });
      setCreateModalOpen(false);
      resetForm();
      setAlertMessage({ type: 'success', message: 'Team created successfully' });
    },
    onError: () => {
      setAlertMessage({ type: 'danger', message: 'Failed to create team' });
    },
  });

  // Update team mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TeamForm> }) =>
      schedulingApi.updateTeam(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedulingTeams'] });
      setEditModalOpen(false);
      setSelectedTeam(null);
      resetForm();
      setAlertMessage({ type: 'success', message: 'Team updated successfully' });
    },
    onError: () => {
      setAlertMessage({ type: 'danger', message: 'Failed to update team' });
    },
  });

  // Delete team mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => schedulingApi.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedulingTeams'] });
      setDeleteModalOpen(false);
      setSelectedTeam(null);
      setAlertMessage({ type: 'success', message: 'Team deleted successfully' });
    },
    onError: () => {
      setAlertMessage({ type: 'danger', message: 'Failed to delete team' });
    },
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: number }) =>
      schedulingApi.addTeamMember(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedulingTeams'] });
      setAddMemberModalOpen(false);
      setSelectedTeam(null);
      setAlertMessage({ type: 'success', message: 'Member added successfully' });
    },
    onError: () => {
      setAlertMessage({ type: 'danger', message: 'Failed to add member' });
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: number }) =>
      schedulingApi.removeTeamMember(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedulingTeams'] });
      setAlertMessage({ type: 'success', message: 'Member removed successfully' });
    },
    onError: () => {
      setAlertMessage({ type: 'danger', message: 'Failed to remove member' });
    },
  });

  const resetForm = () => {
    setName('');
    setDescription('');
    setSlug('');
    setSelectedMembers([]);
    setMeetingTitle('Team Meeting');
    setMeetingLocation('');
    setWelcomeMessage('');
    setDistributionMode('equal');
    setMeetingDuration(30);
  };

  const handleCreateClick = () => {
    resetForm();
    setCreateModalOpen(true);
  };

  const handleEditClick = (team: Team) => {
    setSelectedTeam(team);
    setName(team.name);
    setDescription(team.description);
    setSlug(team.slug);
    setSelectedMembers(team.members || []);
    setMeetingTitle(team.meeting_title);
    setMeetingLocation(team.meeting_location);
    setWelcomeMessage(team.welcome_message);
    setDistributionMode(team.distribution_mode);
    setMeetingDuration(team.default_meeting_duration);
    setEditModalOpen(true);
  };

  const handleCreateSubmit = () => {
    createMutation.mutate({
      name,
      description,
      slug,
      members: selectedMembers,
      distribution_mode: distributionMode,
      default_meeting_duration: meetingDuration,
      meeting_title: meetingTitle,
      meeting_location: meetingLocation,
      welcome_message: welcomeMessage,
      is_active: true,
    });
  };

  const handleUpdateSubmit = () => {
    if (selectedTeam) {
      updateMutation.mutate({
        id: selectedTeam.id,
        data: {
          name,
          description,
          slug,
          members: selectedMembers,
          distribution_mode: distributionMode,
          default_meeting_duration: meetingDuration,
          meeting_title: meetingTitle,
          meeting_location: meetingLocation,
          welcome_message: welcomeMessage,
          is_active: true,
        },
      });
    }
  };

  const handleCopyBookingLink = (team: Team) => {
    if (team.booking_url) {
      navigator.clipboard.writeText(team.booking_url);
      setAlertMessage({ type: 'success', message: 'Booking link copied!' });
    }
  };

  const handleViewStats = (team: Team) => {
    setSelectedTeam(team);
    setStatsModalOpen(true);
  };

  const handleAddMemberClick = (team: Team) => {
    setSelectedTeam(team);
    setAddMemberModalOpen(true);
  };

  const handleRemoveMember = (team: Team, userId: number) => {
    if (window.confirm('Remove this member from the team?')) {
      removeMemberMutation.mutate({ teamId: team.id, userId });
    }
  };

  if (teamsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Button component={Link} to="/operations/scheduling" variant="plain" sx={{mb: 2}}>Back to Calendar</Button>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Team Scheduling</Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-md" color="neutral">
            Manage round-robin scheduling teams
          </Typography>
        </Box>
        <Button startDecorator={<AddIcon />} onClick={handleCreateClick}>
          Create Team
        </Button>
      </Stack>

      {alertMessage && (
        <Alert
          color={alertMessage.type}
          sx={{ mb: 3 }}
          endDecorator={
            <Button size="sm" variant="plain" onClick={() => setAlertMessage(null)}>
              ✕
            </Button>
          }
        >
          {alertMessage.message}
        </Alert>
      )}

      {teams.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <GroupsIcon size={64} color="var(--joy-palette-text-secondary)" />
              <Typography level="title-lg">No Teams Yet</Typography>
              <Typography level="body-sm" color="neutral">
                Create a team to enable round-robin scheduling for your organization.
              </Typography>
              <Button startDecorator={<AddIcon />} onClick={handleCreateClick}>
                Create Your First Team
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={3}>
          {teams.map((team) => (
            <Card key={team.id} variant="outlined">
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                  {/* Left - Team Info */}
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <GroupsIcon size={32} color="var(--joy-palette-primary-500)" />
                      <Box>
                        <Typography level="title-lg">{team.name}</Typography>
                        <Typography level="body-sm" color="neutral">
                          {team.description}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Typography level="body-sm">
                        <strong>URL Slug:</strong> {team.slug}
                      </Typography>
                      <Typography level="body-sm">
                        <strong>Distribution:</strong> {team.distribution_mode_display}
                      </Typography>
                      <Typography level="body-sm">
                        <strong>Meeting Duration:</strong> {team.default_meeting_duration} minutes
                      </Typography>
                      <Typography level="body-sm">
                        <strong>Members:</strong> {team.member_count || 0}
                      </Typography>
                    </Stack>

                    {team.booking_url && (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<CopyIcon />}
                        onClick={() => handleCopyBookingLink(team)}
                      >
                        Copy Booking Link
                      </Button>
                    )}
                  </Box>

                  {/* Vertical divider on desktop, horizontal on mobile */}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Divider orientation="vertical" />
                  </Box>
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Divider />
                  </Box>

                  {/* Right - Members & Actions */}
                  <Box sx={{ minWidth: { xs: 0, md: 300 }, flex: { md: '0 0 300px' } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography level="title-sm">Team Members</Typography>
                      <IconButton size="sm" onClick={() => handleAddMemberClick(team)}>
                        <PersonAddIcon />
                      </IconButton>
                    </Stack>

                    <Box sx={{ mb: 2, maxHeight: 150, overflowY: 'auto' }}>
                      {team.members_details && team.members_details.length > 0 ? (
                        <Stack spacing={0.5}>
                          {team.members_details.map((member) => (
                            <Stack
                              key={member.id}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography level="body-sm">{member.full_name}</Typography>
                              <IconButton
                                size="sm"
                                color="danger"
                                onClick={() => handleRemoveMember(team, member.id)}
                              >
                                <PersonRemoveIcon />
                              </IconButton>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography level="body-xs" color="neutral">
                          No members yet
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }} useFlexGap>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<StatsIcon />}
                        onClick={() => handleViewStats(team)}
                      >
                        Stats
                      </Button>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<EditIcon />}
                        onClick={() => handleEditClick(team)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        variant="outlined"
                        startDecorator={<DeleteIcon />}
                        onClick={() => {
                          setSelectedTeam(team);
                          setDeleteModalOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Create/Edit Team Modal */}
      <Modal
        open={createModalOpen || editModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditModalOpen(false);
          setSelectedTeam(null);
        }}
      >
        <ModalDialog sx={{
          minWidth: { xs: 0, sm: 600 },
          maxWidth: { xs: '100%', sm: 800 },
          width: { xs: 'auto', sm: '100%' },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <ModalClose />
          <DialogTitle>{createModalOpen ? 'Create Team' : 'Edit Team'}</DialogTitle>
          <DialogContent sx={{ overflow: 'visible' }}>
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Team Name</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  minRows={2}
                />
              </FormControl>

              <FormControl required>
                <FormLabel>URL Slug</FormLabel>
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                  placeholder="sales-team"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Team Members</FormLabel>
                <Select
                  multiple
                  value={selectedMembers}
                  onChange={(_, value) => setSelectedMembers(value as number[])}
                  placeholder="Select team members..."
                  renderValue={(selected) => {
                    const values = selected.map(s => s.value);
                    const selectedUsers = orgUsers.filter(u => values.includes(u.id));
                    return selectedUsers.map(u => u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username).join(', ');
                  }}
                >
                  {orgUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Distribution Mode</FormLabel>
                <Select
                  value={distributionMode}
                  onChange={(_, value) => setDistributionMode(value as 'equal' | 'maximize')}
                >
                  <Option value="equal">Equal Distribution (Fair rotation)</Option>
                  <Option value="maximize">Maximize Availability (Show all slots)</Option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Meeting Duration</FormLabel>
                <Select
                  value={meetingDuration}
                  onChange={(_, value) => setMeetingDuration(value as number)}
                >
                  <Option value={15}>15 minutes</Option>
                  <Option value={30}>30 minutes</Option>
                  <Option value={45}>45 minutes</Option>
                  <Option value={60}>60 minutes</Option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Meeting Title</FormLabel>
                <Input value={meetingTitle} onChange={(e) => setMeetingTitle(e.target.value)} />
              </FormControl>

              <FormControl>
                <FormLabel>Meeting Location</FormLabel>
                <Input
                  value={meetingLocation}
                  onChange={(e) => setMeetingLocation(e.target.value)}
                  placeholder="Google Meet, Zoom, or phone"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Welcome Message</FormLabel>
                <Textarea
                  value={welcomeMessage}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                  minRows={2}
                />
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                setCreateModalOpen(false);
                setEditModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={createModalOpen ? handleCreateSubmit : handleUpdateSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {createModalOpen ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Add Member Modal */}
      <Modal open={addMemberModalOpen} onClose={() => setAddMemberModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogContent>
            <FormControl>
              <FormLabel>Select User</FormLabel>
              <Select
                placeholder="Choose a user..."
                onChange={(_, value) => {
                  if (value && selectedTeam) {
                    addMemberMutation.mutate({ teamId: selectedTeam.id, userId: value as number });
                  }
                }}
              >
                {orgUsers
                  .filter(
                    (user) =>
                      !selectedTeam?.members_details?.some((m) => m.id === user.id)
                  )
                  .map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </Option>
                  ))}
              </Select>
            </FormControl>
          </DialogContent>
        </ModalDialog>
      </Modal>

      {/* Stats Modal */}
      <Modal open={statsModalOpen} onClose={() => setStatsModalOpen(false)}>
        <ModalDialog sx={{ minWidth: { xs: 0, sm: 600 }, maxWidth: { xs: '100%', sm: '90vw' }, width: { xs: 'auto', sm: '100%' }, maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <DialogTitle>Team Statistics - {selectedTeam?.name}</DialogTitle>
          <DialogContent>
            {statsLoading ? (
              <CircularProgress />
            ) : teamStats ? (
              <Box>
                <Typography level="body-sm" sx={{ mb: 2 }}>
                  Last {teamStats.days} days
                </Typography>
                <Sheet variant="outlined" sx={{ borderRadius: 'sm' }}>
                  <ResponsiveTable<typeof teamStats.members[number]>
                    rows={teamStats.members}
                    getRowKey={(m) => m.member_id}
                    columns={[
                      {
                        key: 'member',
                        sortValue: (row) => row.member_name,
                        header: 'Member',
                        mobileVariant: 'header',
                        render: (m) => <Typography level="title-sm">{m.member_name}</Typography>,
                      },
                      {
                        key: 'scheduled',
                        sortValue: (row) => row.scheduled,
                        header: 'Scheduled',
                        render: (m) => <Typography level="body-sm">{m.scheduled}</Typography>,
                      },
                      {
                        key: 'completed',
                        sortValue: (row) => row.completed,
                        header: 'Completed',
                        render: (m) => <Typography level="body-sm">{m.completed}</Typography>,
                      },
                      {
                        key: 'cancelled',
                        sortValue: (row) => row.cancelled,
                        header: 'Cancelled',
                        render: (m) => <Typography level="body-sm">{m.cancelled}</Typography>,
                      },
                      {
                        key: 'total',
                        header: 'Total',
                        render: (m) => <Typography level="body-sm" fontWeight="lg">{m.total}</Typography>,
                      },
                    ]}
                  />
                </Sheet>
              </Box>
            ) : (
              <Typography>No statistics available</Typography>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Delete Team</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>{selectedTeam?.name}</strong>?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => selectedTeam && deleteMutation.mutate(selectedTeam.id)}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
